/**
 * Aggregates over workflow execution rows (the
 * `agent/workflows/admin/instances/:key` response) — completion /
 * feedback-rating summaries grouped by entry path and by start context,
 * plus a last-30-days execution series.
 *
 * Shared by the workflow editor's Stats tab (cAdminAtlasWorkflows) and
 * the usage page's per-workflow row expansion (cAdminAtlasUsage).
 */
export function computeInstanceStats(rows) {
  const all = rows || [];
  const summarise = (group) => {
    const total = group.length;
    const completed = group.filter((r) => r.status === 'completed').length;
    const rated = group.filter((r) => r.rating);
    const avg =
      rated.length > 0
        ? Math.round(
            (rated.reduce((sum, r) => sum + r.rating.score, 0) / rated.length) * 10,
          ) / 10
        : null;
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
    return {
      total,
      completed,
      completedPct: pct(completed),
      rated: rated.length,
      ratedPct: pct(rated.length),
      avgRating: avg,
    };
  };

  // Per entry path (the start step's recorded choice).
  const byPathMap = new Map();
  for (const r of all) {
    const path = r.choice || '(no choice recorded)';
    if (!byPathMap.has(path)) byPathMap.set(path, []);
    byPathMap.get(path).push(r);
  }
  const byPath = Array.from(byPathMap.entries())
    .map(([path, group]) => ({ path, ...summarise(group) }))
    .sort((a, b) => b.total - a.total);

  // Per start context: the portal route (page), else the non-portal
  // surface (mcp-app…). Pre-provenance instances show unrecorded.
  const contextGroup = (ctx) => {
    if (!ctx) return '(not recorded)';
    if (ctx.route) return ctx.route;
    if (ctx.surface) return ctx.surface;
    return '(not recorded)';
  };
  const byContextMap = new Map();
  for (const r of all) {
    const key = contextGroup(r.context);
    if (!byContextMap.has(key)) byContextMap.set(key, []);
    byContextMap.get(key).push(r);
  }
  const byContext = Array.from(byContextMap.entries())
    .map(([path, group]) => ({ path, ...summarise(group) }))
    .sort((a, b) => b.total - a.total);

  // Executions per day, last 30 days (inclusive of today).
  const dayKey = (d) => {
    const mm = `${d.getMonth() + 1}`.padStart(2, '0');
    const dd = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  };
  const counts = new Map();
  for (const r of all) {
    if (!r.created) continue;
    const created = new Date(r.created);
    if (Number.isNaN(created.getTime())) continue;
    const key = dayKey(created);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const byDay = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    const key = dayKey(d);
    byDay.push({ day: key, count: counts.get(key) || 0 });
  }
  const maxDay = Math.max(1, ...byDay.map((d) => d.count));

  return { ...summarise(all), byPath, byContext, byDay, maxDay };
}
